import * as Yup from "yup";

const phoneRegex = RegExp(/^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/); // xxx-xxx-xxxx
const addressRegex = RegExp(/^[A-Za-z0-9'.-\s,]*$/); // No special characters
const nameRegex = RegExp(/^[A-Za-z'.-\s]*$/); // No special characters, no numbers
const cityRegex = RegExp(/^[a-zA-Z]+(?:[\s-][a-zA-Z]+)*$/); // No numbers, no special characters except -
export const INITIAL_VALUES = {
    firstName: "",
    lastName: "",
    address: "",
    city: "",
    state: "",
    zip: "",
    phone: "",
    email: "",
    serial: "",
    installMonth: "",
    installYear: "",
    textOptIn: null,
    status: "",
    isOriginalPurchaser: "",
    details: "",
    hasContactedFactory: "",
    images: [],
    wantsKeys: "",
};

export const VALIDATION_SCHEMA = Yup.object().shape({
    firstName: Yup.string().matches(nameRegex, "Invalid first name").trim().required("First name is required"),

    lastName: Yup.string()
        .trim()
        .matches(nameRegex, "Invalid last name")
        .min(2, "Your last name is too short")
        .required("Last name is required"),

    address: Yup.string()
        .trim()
        .min(5, "Address too short")
        .matches(addressRegex, "Invalid address")
        .when("status", {
            is: "Purchased, not yet installed" || "Purchased and installed",
            then: Yup.string().required("Address is required"),
        }),

    city: Yup.string()
        .trim()
        .min(2, "Name too short")
        .matches(cityRegex, "Invalid city name"),

    phone: Yup.string().matches(phoneRegex, "Invalid phone"),

    zip: Yup.string()
        .length(5, "Invalid zip code")
        .required("Zip code is required"),

    email: Yup.string()
        .trim()
        .email("The email is invalid")
        .required("Email address is required"),

    textOptIn: Yup.string()
        .matches(/^Yes|No/)
        .nullable()
        .when("phone", {
            is: (val) => val && val.length > 1,
            then: Yup.string().required(
                "Please indicate if we can text you at this number"
            ),
        }),

    status: Yup.string().required(
        "Please indicate the status of your building"
    ),

    isOriginalPurchaser: Yup.string()
        .matches(/^Yes|No/)
        .when("status", {
            is: "Purchased and installed",
            then: Yup.string().required(
                "Please indicate if you are the original purchaser"
            ),
        }),

    details: Yup.string()
        .trim()
        .min(5, "Please be as specific as possible")
        .when("status", {
            is: "Purchased, not yet installed" || "Purchased and installed",
            then: Yup.string().required("Details are required"),
        }),

    hasContactedFactory: Yup.string().when("status", {
        is: "Purchased, not yet installed",
        then: Yup.string().required(
            "Please indicate if you have contacted your local factory"
        ),
    }),
});

export const months = [
    {
        abbreviation: "Jan",
        name: "1 - January",
    },
    {
        abbreviation: "Feb",
        name: "2 - February",
    },
    {
        abbreviation: "Mar",
        name: "3 - March",
    },
    {
        abbreviation: "Apr",
        name: "4 - April",
    },
    {
        abbreviation: "May",
        name: "5 - May",
    },
    {
        abbreviation: "Jun",
        name: "6 - June",
    },
    {
        abbreviation: "Jul",
        name: "7 - July",
    },
    {
        abbreviation: "Aug",
        name: "8 - August",
    },
    {
        abbreviation: "Sep",
        name: "9 - September",
    },
    {
        abbreviation: "Oct",
        name: "10 - October",
    },
    {
        abbreviation: "Nov",
        name: "11 - November",
    },
    {
        abbreviation: "Dec",
        name: "12 - December",
    },
];

export const states = [
    {
        name: "Alabama",
        abbreviation: "AL",
    },
    {
        name: "Alaska",
        abbreviation: "AK",
    },
    {
        name: "Arizona",
        abbreviation: "AZ",
    },
    {
        name: "Arkansas",
        abbreviation: "AR",
    },
    {
        name: "California",
        abbreviation: "CA",
    },
    {
        name: "Colorado",
        abbreviation: "CO",
    },
    {
        name: "Connecticut",
        abbreviation: "CT",
    },
    {
        name: "Delaware",
        abbreviation: "DE",
    },
    {
        name: "District of Columbia",
        abbreviation: "DC",
    },
    {
        name: "Florida",
        abbreviation: "FL",
    },
    {
        name: "Georgia",
        abbreviation: "GA",
    },
    {
        name: "Hawaii",
        abbreviation: "HI",
    },
    {
        name: "Idaho",
        abbreviation: "ID",
    },
    {
        name: "Illinois",
        abbreviation: "IL",
    },
    {
        name: "Indiana",
        abbreviation: "IN",
    },
    {
        name: "Iowa",
        abbreviation: "IA",
    },
    {
        name: "Kansas",
        abbreviation: "KS",
    },
    {
        name: "Kentucky",
        abbreviation: "KY",
    },
    {
        name: "Louisiana",
        abbreviation: "LA",
    },
    {
        name: "Maine",
        abbreviation: "ME",
    },
    {
        name: "Maryland",
        abbreviation: "MD",
    },
    {
        name: "Massachusetts",
        abbreviation: "MA",
    },
    {
        name: "Michigan",
        abbreviation: "MI",
    },
    {
        name: "Minnesota",
        abbreviation: "MN",
    },
    {
        name: "Mississippi",
        abbreviation: "MS",
    },
    {
        name: "Missouri",
        abbreviation: "MO",
    },
    {
        name: "Montana",
        abbreviation: "MT",
    },
    {
        name: "Nebraska",
        abbreviation: "NE",
    },
    {
        name: "Nevada",
        abbreviation: "NV",
    },
    {
        name: "New Hampshire",
        abbreviation: "NH",
    },
    {
        name: "New Jersey",
        abbreviation: "NJ",
    },
    {
        name: "New Mexico",
        abbreviation: "NM",
    },
    {
        name: "New York",
        abbreviation: "NY",
    },
    {
        name: "North Carolina",
        abbreviation: "NC",
    },
    {
        name: "North Dakota",
        abbreviation: "ND",
    },
    {
        name: "Ohio",
        abbreviation: "OH",
    },
    {
        name: "Oklahoma",
        abbreviation: "OK",
    },
    {
        name: "Oregon",
        abbreviation: "OR",
    },
    {
        name: "Pennsylvania",
        abbreviation: "PA",
    },
    {
        name: "Rhode Island",
        abbreviation: "RI",
    },
    {
        name: "South Carolina",
        abbreviation: "SC",
    },
    {
        name: "South Dakota",
        abbreviation: "SD",
    },
    {
        name: "Tennessee",
        abbreviation: "TN",
    },
    {
        name: "Texas",
        abbreviation: "TX",
    },
    {
        name: "Utah",
        abbreviation: "UT",
    },
    {
        name: "Vermont",
        abbreviation: "VT",
    },
    {
        name: "Virginia",
        abbreviation: "VA",
    },
    {
        name: "Washington",
        abbreviation: "WA",
    },
    {
        name: "West Virginia",
        abbreviation: "WV",
    },
    {
        name: "Wisconsin",
        abbreviation: "WI",
    },
    {
        name: "Wyoming",
        abbreviation: "WY",
    },
];
